"use client";
import React from "react";
import dynamic from "next/dynamic";
import MonarchResolver from "@/monarch/MonarchResolver";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { FindYourPathTestResult } from "@/monarch/rules/findYourPath/types";
import { LearnPageRedesignTestResult } from "@/monarch/rules/learnPageRedesign/types";
import { GuidedPathVsDynamicFlowResult } from "@/monarch/rules/guidedPathVsDynamicFlow/types";
import GuidedPathWidget from "./GuidedPathWidget";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { VARIANT_TYPES, VariantType } from "./type";

const DynamicFlowWrapper = dynamic(
  () => import("@/components/DynamicFlowWrapper"),
  { ssr: false },
);

interface GuidedPathProps {
  variant?: VariantType;
  className?: string;
  location?: string;
}

const GuidedPath = ({
  variant = VARIANT_TYPES.default,
  className = "",
  location,
}: GuidedPathProps) => {
  const guidedPathComponent = (
    <MonarchResolver<FindYourPathTestResult>
      experimentKey="useFindYourPath"
      variants={{
        control: (
          <div className={className}>
            <GuidedPathWidget />
          </div>
        ),
        emptyState: <div></div>,
        test: (
          <div className={className}>
            <MonarchDataConsumer<FindYourPathTestResult>
              experimentKey="useFindYourPath"
              render={(testResult) => {
                const layoutVariant = testResult?.data?.layoutVariant;
                const firstView = testResult?.data?.firstView;
                return (
                  <GuidedPathWidget
                    layoutVariant={layoutVariant ?? "default"}
                    firstView={firstView ?? "entry"}
                  />
                );
              }}
            />
          </div>
        ),
        customConfig: (
          <div className={className}>
            <MonarchDataConsumer<FindYourPathTestResult>
              experimentKey="useFindYourPath"
              render={(testResult) => {
                const customSections = testResult?.data?.sections;
                const layoutVariant = testResult?.data?.layoutVariant;
                const firstView = testResult?.data?.firstView;
                return (
                  <GuidedPathWidget
                    customQuizSections={customSections}
                    layoutVariant={layoutVariant ?? "default"}
                    firstView={firstView ?? "entry"}
                  />
                );
              }}
            />
          </div>
        ),
      }}
      fallbackVariant="control"
    />
  );

  const originalComponent = (
    <MonarchResolver<GuidedPathVsDynamicFlowResult>
      experimentKey="useGuidedPathVsDynamicFlow"
      variants={{
        control: guidedPathComponent,
        test: (
          <MonarchDataConsumer<GuidedPathVsDynamicFlowResult>
            experimentKey="useGuidedPathVsDynamicFlow"
            render={(testResult) => (
              <DynamicFlowWrapper
                placement="dashboard"
                forcedFlowId={testResult?.data?.flowId ?? "find-your-path"}
                styleVariant="alt"
              />
            )}
          />
        ),
      }}
      fallbackVariant="control"
    />
  );

  // Only apply learn page redesign feature flag when location is "learn-page"
  if (location === "learn-page") {
    return (
      <FeatureFlagComponentResolverClient
        flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
        variants={{
          legacy: null,
          test: (
            <MonarchResolver<LearnPageRedesignTestResult>
              experimentKey="useLearnPageRedesign"
              variants={{
                control: null,
                test: (
                  <MonarchDataConsumer<LearnPageRedesignTestResult>
                    experimentKey="useLearnPageRedesign"
                    render={(testResult) => {
                      if (
                        !testResult?.data?.findYourPath ||
                        !testResult.data.findYourPath?.enabled
                      ) {
                        return null;
                      }
                      return originalComponent;
                    }}
                  />
                ),
              }}
              Loader={null}
              fallbackVariant="control"
            />
          ),
        }}
        fallback="legacy"
      />
    );
  }

  return originalComponent;
};

export default GuidedPath;
